import React, { useState, useEffect } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import InputAdornment from '@mui/material/InputAdornment';
import { getMarketplaceRegions, getMarketplaceCities } from '../api';

const NICHES = [
  { id: "windows/repair", name: "Окна — Ремонт/Сервис/Модернизация" },
  { id: "windows/single", name: "Окна — Самовывоз без монтажа/1 конструкция" },
  { id: "windows/multiple", name: "Окна — Установка от 2-х конструкций/остекление" },
  { id: "windows/house", name: "Окна — Остекление коттеджа/дома/террасы" },
  { id: "windows/balcony_finish", name: "Окна — Остекление лоджии/балкона+отделка" },
  { id: "windows/commercial", name: "Окна — Остекление коммерческих объектов" },
];

interface PublishMarketplaceModalProps {
  open: boolean;
  onClose: () => void;
  leadIds: number[];
  onPublish: (leadIds: number[], options: {
    region?: string;
    city?: string;
    niche?: string;
    price: number;
  }) => Promise<void>;
}

export const PublishMarketplaceModal: React.FC<PublishMarketplaceModalProps> = ({
  open,
  onClose,
  leadIds,
  onPublish,
}) => {
  const [region, setRegion] = useState<string | null>(null);
  const [city, setCity] = useState<string | null>(null);
  const [niche, setNiche] = useState('');
  const [price, setPrice] = useState('1');
  const [loading, setLoading] = useState(false);

  const [regions, setRegions] = useState<string[]>([]);
  const [regionsLoading, setRegionsLoading] = useState(false);
  const [cities, setCities] = useState<string[]>([]);
  const [citiesLoading, setCitiesLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setRegionsLoading(true);
    getMarketplaceRegions()
      .then((data) => setRegions(data.regions.map((r) => r.name)))
      .catch(() => setRegions([]))
      .finally(() => setRegionsLoading(false));
  }, [open]);

  useEffect(() => {
    setCity(null);
    setCities([]);
    if (!region) return;
    setCitiesLoading(true);
    getMarketplaceCities(region)
      .then((data) => setCities(data.cities.map((c) => c.name)))
      .catch(() => setCities([]))
      .finally(() => setCitiesLoading(false));
  }, [region]);

  const priceNum = parseFloat(price) || 0;
  const isPriceValid = priceNum > 0;

  const handlePublish = async () => {
    if (!isPriceValid) return;
    setLoading(true);
    try {
      await onPublish(leadIds, {
        region: region || undefined,
        city: city || undefined,
        niche: niche || undefined,
        price: priceNum,
      });
      resetForm();
      onClose();
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setRegion(null);
    setCity(null);
    setNiche('');
    setPrice('1');
  };

  const handleClose = () => {
    if (!loading) {
      resetForm();
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Опубликовать на маркетплейс</DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Будет опубликовано лидов: <strong>{leadIds.length}</strong>
          </Typography>

          <TextField
            label="Цена"
            type="number"
            value={price}
            onChange={(e) => setPrice(e.target.value)}
            error={!isPriceValid && price !== ''}
            helperText={!isPriceValid && price !== '' ? 'Цена должна быть больше 0' : undefined}
            InputProps={{
              endAdornment: <InputAdornment position="end">&#8381;</InputAdornment>,
              inputProps: { min: 0.1, step: 0.1 },
            }}
          />

          <Autocomplete
            options={regions}
            value={region}
            onChange={(_, val) => setRegion(val)}
            loading={regionsLoading}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Регион"
                placeholder="Выберите регион..."
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {regionsLoading ? <CircularProgress size={18} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />

          <Autocomplete
            options={cities}
            value={city}
            onChange={(_, val) => setCity(val)}
            loading={citiesLoading}
            disabled={!region || cities.length === 0}
            noOptionsText={region ? 'Нет городов' : 'Сначала выберите регион'}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Город"
                placeholder={region ? 'Выберите город...' : 'Сначала выберите регион'}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {citiesLoading ? <CircularProgress size={18} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />

          <FormControl fullWidth>
            <InputLabel>Ниша</InputLabel>
            <Select
              value={niche}
              label="Ниша"
              onChange={(e) => setNiche(e.target.value)}
            >
              <MenuItem value="">Не указана</MenuItem>
              {NICHES.map((n) => (
                <MenuItem key={n.id} value={n.id}>{n.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>Отмена</Button>
        <Button
          variant="contained"
          onClick={handlePublish}
          disabled={loading || leadIds.length === 0 || !isPriceValid}
          startIcon={loading ? <CircularProgress size={18} /> : undefined}
        >
          {loading ? 'Публикация...' : 'Опубликовать'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
